import { lazy, Suspense } from "react";
import { Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import {
  dataProvider,
  authProvider,
  notificationProvider,
  dataProviderQueryOptions,
} from "@/providers";
import { AIRTABLE_TABLES } from "@/types";

// Lazy-loaded page components for code splitting
const HomePage = lazy(() => import("@/pages/HomePage"));
const VoiceAICoachingPage = lazy(() => import("@/pages/voice-ai-coaching"));
const CallReportsPage = lazy(() => import("@/pages/call-reports"));
const CallReportDetailPage = lazy(() => import("@/pages/call-reports/[id]"));
const MyCreditsPage = lazy(() => import("@/pages/my-credits"));
const ClientsJourneyPage = lazy(() => import("@/pages/clients-journey"));
const ClientsJourneyDetailsPage = lazy(
  () => import("@/pages/clients-journey-details")
);
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const BuyCreditsPage = lazy(() => import("@/pages/buy-credits"));
const AccountPage = lazy(() => import("@/pages/account"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const LoginPage = lazy(() => import("@/pages/login"));
const SignUpPage = lazy(() => import("@/pages/sign-up"));
const CheckoutRedirectPage = lazy(() => import("@/pages/checkout-redirect"));
const CancelPage = lazy(() => import("@/pages/cancel"));

// AuthCallbackPage needs special handling due to named export
const AuthCallbackPage = lazy(() =>
  import("@/pages/auth").then((module) => ({ default: module.AuthCallbackPage }))
);

/**
 * Loading fallback component for Suspense boundaries
 */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="flex flex-col items-center gap-4">
        {/* Animated loading spinner */}
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-4 border-slate-700 rounded-full" />
          <div className="absolute inset-0 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin" />
        </div>
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Refine resources configuration
 * Maps to the 5 Airtable tables with proper identifiers
 */
const resources = [
  {
    name: AIRTABLE_TABLES.CLIENT_DATABASE,
    meta: {
      label: "Clients",
    },
  },
  {
    name: AIRTABLE_TABLES.SUBMISSION_DATA,
    meta: {
      label: "Submissions",
    },
  },
  {
    name: AIRTABLE_TABLES.CALL_DATA,
    meta: {
      label: "Calls",
    },
  },
  {
    name: AIRTABLE_TABLES.CREDITS_LEDGER,
    meta: {
      label: "Credits",
    },
  },
  {
    name: AIRTABLE_TABLES.CLIENT_JOURNEY,
    meta: {
      label: "Journey",
    },
  },
];

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Refine
          dataProvider={dataProvider}
          authProvider={authProvider}
          notificationProvider={notificationProvider}
          routerProvider={routerProvider}
          resources={resources}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            reactQuery: {
              clientConfig: {
                defaultOptions: {
                  queries: dataProviderQueryOptions,
                },
              },
            },
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route
                path="/"
                element={
                  <RouteErrorBoundary routePath="/">
                    <HomePage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/pricing"
                element={
                  <RouteErrorBoundary routePath="/pricing">
                    <PricingPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/buy-credits"
                element={
                  <RouteErrorBoundary routePath="/buy-credits">
                    <BuyCreditsPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/login"
                element={
                  <RouteErrorBoundary routePath="/login">
                    <LoginPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/sign-up"
                element={
                  <RouteErrorBoundary routePath="/sign-up">
                    <SignUpPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/auth/callback"
                element={
                  <RouteErrorBoundary routePath="/auth/callback">
                    <AuthCallbackPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/cancel"
                element={
                  <RouteErrorBoundary routePath="/cancel">
                    <CancelPage />
                  </RouteErrorBoundary>
                }
              />
              {/* Redirect /register to /sign-up for backwards compatibility */}
              <Route path="/register" element={<Navigate to="/sign-up" replace />} />

              {/* Checkout routes (protected) */}
              <Route
                path="/checkout-redirect"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/checkout-redirect">
                      <CheckoutRedirectPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Protected routes */}
              <Route
                path="/voice-ai-coaching"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/voice-ai-coaching">
                      <VoiceAICoachingPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/call-reports"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/call-reports">
                      <CallReportsPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/call-reports/:id"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/call-reports/:id">
                      <CallReportDetailPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-credits"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/my-credits">
                      <MyCreditsPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/account">
                      <AccountPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients-journey"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/clients-journey">
                      <ClientsJourneyPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients-journey-details/:id"
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routePath="/clients-journey-details/:id">
                      <ClientsJourneyDetailsPage />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Refine>
        <SpeedInsights />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
